import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getAdminWorlds, getAdminWorldDetail, reindexAdminWorld, revoiceAdminWorld, branchAdminWorld } from '../../api';
import type { AdminBranchResponse, AdminReindexResponse, AdminRevoiceResponse } from '../../api';
import { AdminWorldDetail, AdminWorldSummary } from '../../types';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { InspectModal } from '../../components/InspectModal';

export function AdminWorlds() {
  const navigate = useNavigate();
  const location = useLocation();
  const { worldId } = useParams<{ worldId?: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [worlds, setWorlds] = useState<AdminWorldSummary[]>([]);
  const [worldDetail, setWorldDetail] = useState<AdminWorldDetail | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inspectLogId, setInspectLogId] = useState<string | null>(null);
  const [reindexBusy, setReindexBusy] = useState(false);
  const [reindexResult, setReindexResult] = useState<AdminReindexResponse | null>(null);
  const [reindexError, setReindexError] = useState<string | null>(null);
  const [reindexProgress, setReindexProgress] = useState<{ totalIndexed: number; totalPairs: number; batchesRun: number } | null>(null);
  const reindexAbortRef = React.useRef(false);
  const [revoiceBusy, setRevoiceBusy] = useState(false);
  const [revoiceStarted, setRevoiceStarted] = useState<AdminRevoiceResponse | null>(null);
  const [revoiceError, setRevoiceError] = useState<string | null>(null);
  const [branchBusy, setBranchBusy] = useState(false);
  const [branchMade, setBranchMade] = useState<AdminBranchResponse | null>(null);
  const [branchError, setBranchError] = useState<string | null>(null);
  const limit = 20;

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      if (worldId) {
        loadWorldDetail(worldId);
      } else {
        loadWorlds();
      }
    }
  }, [isAuthenticated, user, page, worldId, includeDeleted]);

  const loadWorlds = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminWorlds(limit, page * limit, undefined, includeDeleted);
      setWorlds(data.worlds);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load worlds');
    } finally {
      setLoading(false);
    }
  };

  const loadWorldDetail = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminWorldDetail(id);
      setWorldDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load world');
    } finally {
      setLoading(false);
    }
  };

  const formatCost = (micros: number) => `$${(micros / 1_000_000).toFixed(4)}`;
  const formatDate = (date: string) => new Date(date).toLocaleDateString();

  const handleRevoice = async (id: string) => {
    const ok = window.confirm(
      'Rewrite every character\'s voice? Each stored voice is replaced with a new one written from the character\'s life facts, so the cast will sound different from the next turn on. Billed to you, a few cents per world.',
    );
    if (!ok) return;
    setRevoiceBusy(true);
    setRevoiceStarted(null);
    setRevoiceError(null);
    try {
      setRevoiceStarted(await revoiceAdminWorld(id));
    } catch (err) {
      setRevoiceError(err instanceof Error ? err.message : String(err));
    } finally {
      setRevoiceBusy(false);
    }
  };

  const handleBranch = async (id: string) => {
    setBranchBusy(true);
    setBranchMade(null);
    setBranchError(null);
    try {
      setBranchMade(await branchAdminWorld(id));
    } catch (err) {
      setBranchError(err instanceof Error ? err.message : String(err));
    } finally {
      setBranchBusy(false);
    }
  };

  const handleReindex = async (id: string) => {
    const ok = window.confirm(
      'Reindex this world? Batched through Workers AI embedding + Vectorize upsert. Idempotent, resumable. Big worlds (1000+ messages) take ~2 minutes across batches; browser must stay open.',
    );
    if (!ok) return;
    setReindexBusy(true);
    setReindexResult(null);
    setReindexError(null);
    setReindexProgress(null);
    reindexAbortRef.current = false;

    // Aggregate counts across all batches for the final result banner.
    let cumIndexed = 0;
    let cumSkipped = 0;
    let cumFailed = 0;
    let cumReasons: Record<string, number> = {};
    let messageCount = 0;
    let batchesRun = 0;
    // Smaller batch for Phase 3+ because preamble generation is LLM-bound
    // (~3s per chunk before we parallelize). 12 keeps each HTTP request
    // under the Workers CPU limit and delivers a progress update every
    // ~10s instead of every 2 minutes.
    const batchSize = 12;

    try {
      while (!reindexAbortRef.current) {
        const res = await reindexAdminWorld(id, batchSize);
        cumIndexed += res.indexed;
        cumSkipped = res.skipped; // monotonic — last one wins, represents covered rows
        cumFailed += res.failed;
        for (const [k, v] of Object.entries(res.reasons)) {
          cumReasons[k] = (cumReasons[k] ?? 0) + v;
        }
        messageCount = res.messageCount;
        batchesRun++;
        setReindexProgress({
          totalIndexed: res.totalIndexed,
          totalPairs: res.totalPairs,
          batchesRun,
        });
        if (res.remaining === 0) break;
        // Trivial delay keeps us from hammering — batches take ~5-10s
        // anyway, so this is mostly for UI smoothness.
        await new Promise((r) => setTimeout(r, 100));
      }
      setReindexResult({
        worldId: id,
        messageCount,
        indexed: cumIndexed,
        skipped: cumSkipped,
        failed: cumFailed,
        reasons: cumReasons,
        remaining: 0,
        totalIndexed: reindexProgress?.totalIndexed ?? 0,
        totalPairs: reindexProgress?.totalPairs ?? 0,
      });
    } catch (err) {
      setReindexError(err instanceof Error ? err.message : 'Reindex failed');
    } finally {
      setReindexBusy(false);
    }
  };

  const handleAbortReindex = () => {
    reindexAbortRef.current = true;
  };
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/admin/worlds');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  // World Detail View
  if (worldId && worldDetail) {
    return (
      <AdminLayout
        title={worldDetail.world.title}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Worlds', to: '/admin/worlds' },
          { label: worldDetail.world.title },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <button onClick={handleBack} className="btn-secondary">
              Return
            </button>
            <Link
              to={`/admin/worlds/${worldId}/traces`}
              className="btn-secondary flex items-center gap-2"
            >
              <span>🔍</span>
              <span>Agent Traces</span>
            </Link>
            {reindexBusy ? (
              <>
                <div className="flex items-center gap-2 text-xs font-mono text-dark-200 bg-dark-700 border border-dark-600 rounded px-3 py-2">
                  <span className="animate-pulse">⏳</span>
                  <span>
                    {reindexProgress
                      ? `${reindexProgress.totalIndexed}/${reindexProgress.totalPairs} pairs · batch ${reindexProgress.batchesRun}`
                      : 'Starting…'}
                  </span>
                </div>
                <button
                  onClick={handleAbortReindex}
                  className="btn-secondary text-sm"
                  title="Stop after the current batch finishes. Progress is saved; you can resume later."
                >
                  Stop
                </button>
              </>
            ) : (
              <button
                onClick={() => handleReindex(worldId)}
                className="btn-secondary flex items-center gap-2"
                title="Embed this world's turn-pairs into Vectorize for T3 retrieval. Batched + resumable."
              >
                <span>🧠</span>
                <span>Reindex retrieval</span>
              </button>
            )}
            <button
              onClick={() => handleRevoice(worldId)}
              disabled={revoiceBusy}
              className="btn-secondary flex items-center gap-2"
              title="Replace every character's stored voice with one the story profiler writes from their life facts."
            >
              <span>🗣️</span>
              <span>Rewrite voices</span>
            </button>
            <button
              onClick={() => handleBranch(worldId)}
              disabled={branchBusy}
              className="btn-secondary flex items-center gap-2"
              title="Branch this world at its latest message into your own account: story, cast, memory and retrieval as they stand now."
            >
              <span>🌿</span>
              <span>{branchBusy ? 'Copying…' : 'Copy to my account'}</span>
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Reindex feedback */}
          {reindexResult && (
            <div className="border-l-2 border-emerald-400 bg-emerald-500/10 px-4 py-3 rounded-r">
              <div className="text-sm text-emerald-200 font-medium mb-1">
                Reindex complete — {reindexResult.indexed} newly indexed, {reindexResult.skipped} already covered, {reindexResult.failed} failed
              </div>
              <div className="text-xs text-emerald-300/80 font-mono">
                messages: {reindexResult.messageCount}
                {reindexResult.failed > 0 && (
                  <> · failures: {Object.entries(reindexResult.reasons).map(([k, v]) => `${k}=${v}`).join(', ')}</>
                )}
              </div>
            </div>
          )}
          {revoiceStarted && (
            <div className="border-l-2 border-emerald-400 bg-emerald-500/10 px-4 py-3 rounded-r">
              <div className="text-sm text-emerald-200 font-medium mb-1">
                Rewriting {revoiceStarted.characters.length} voices. Each new voice appears under Agent Traces as character_revoice; reload to see them here.
              </div>
              <div className="text-xs text-emerald-300/80 font-mono">{revoiceStarted.characters.join(', ')}</div>
            </div>
          )}
          {branchMade && (
            <div className="border-l-2 border-emerald-400 bg-emerald-500/10 px-4 py-3 rounded-r text-sm text-emerald-200">
              Copied {branchMade.messageCount} messages through turn {branchMade.turn}.{' '}
              <Link to={`/world/${branchMade.worldId}`} className="underline">Open the copy</Link>
            </div>
          )}
          {branchError && (
            <div className="border-l-2 border-red-400 bg-red-500/10 px-4 py-3 text-sm text-red-200 rounded-r">
              Copy failed: {branchError}
            </div>
          )}
          {revoiceError && (
            <div className="border-l-2 border-red-400 bg-red-500/10 px-4 py-3 text-sm text-red-200 rounded-r">
              Rewrite voices failed: {revoiceError}
            </div>
          )}
          {reindexError && (
            <div className="border-l-2 border-red-400 bg-red-500/10 px-4 py-3 text-sm text-red-200 rounded-r">
              Reindex failed: {reindexError}
            </div>
          )}

          {/* World Info */}
          <Section title="World Info">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="ID" value={worldDetail.world.id} />
              <InfoItem label="Status" value={worldDetail.world.status} />
              <InfoItem label="Messages" value={worldDetail.world.message_count} />
              <InfoItem label="Created" value={formatDate(worldDetail.world.created_at)} />
            </div>
          </Section>

          {/* Seed */}
          {worldDetail.seed && (
            <Section title="World Seed" defaultOpen={false}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <InfoItem label="Genre" value={worldDetail.seed.genre} />
                <InfoItem label="Tone" value={worldDetail.seed.tone} />
                <InfoItem label="Provider" value={worldDetail.seed.provider} />
                <InfoItem label="Model" value={worldDetail.seed.model} />
                <InfoItem label="Content Rating" value={worldDetail.seed.content_rating || 'standard'} />
                <InfoItem label="Allow NSFW" value={worldDetail.seed.allow_nsfw ? 'Yes' : 'No'} />
              </div>
              
              {worldDetail.seed.setting_definition && (
                <div className="bg-dark-700 rounded p-4 mb-4">
                  <h4 className="text-dark-400 text-sm mb-2">Setting Definition</h4>
                  <p className="text-dark-200 whitespace-pre-wrap">{worldDetail.seed.setting_definition}</p>
                </div>
              )}
              
              {worldDetail.seed.initial_situation && (
                <div className="bg-dark-700 rounded p-4 mb-4">
                  <h4 className="text-dark-400 text-sm mb-2">Initial Situation (Opening Hook)</h4>
                  <p className="text-dark-200 whitespace-pre-wrap">{worldDetail.seed.initial_situation}</p>
                </div>
              )}
              
              <div className="bg-dark-700 rounded p-4">
                <h4 className="text-dark-400 text-sm mb-2">Original Prompt</h4>
                <p className="text-dark-200 whitespace-pre-wrap">{worldDetail.seed.original_prompt}</p>
              </div>
            </Section>
          )}

          {/* Companion State */}
          {worldDetail.companionProfile && (
            <Section title="Companion State" defaultOpen={false}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <InfoItem label="Name" value={worldDetail.companionProfile.name} />
                <InfoItem label="Role" value={worldDetail.companionProfile.role ?? 'unknown'} />
                <InfoItem label="Pronouns" value={worldDetail.companionProfile.pronouns ?? 'n/a'} />
                <InfoItem label="Safety" value={(() => {
                  try {
                    const boundaries = worldDetail.companionProfile?.boundaries_json
                      ? JSON.parse(worldDetail.companionProfile.boundaries_json)
                      : null;
                    return boundaries?.safetyMode ?? 'standard';
                  } catch {
                    return 'standard';
                  }
                })()} />
              </div>

              {worldDetail.companionSummary?.rolling_summary && (
                <div className="bg-dark-700 rounded p-4 mb-4">
                  <h4 className="text-dark-400 text-sm mb-2">Rolling Summary</h4>
                  <p className="text-dark-200 whitespace-pre-wrap">
                    {worldDetail.companionSummary.rolling_summary}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-dark-400 text-sm mb-2">Memories ({worldDetail.companionMemories?.length || 0})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {worldDetail.companionMemories?.map((memory) => (
                      <div key={memory.id} className="bg-dark-700 rounded p-3">
                        <div className="text-xs text-dark-400">{memory.type}</div>
                        <div className="text-sm text-dark-200 font-semibold">{memory.key}</div>
                        <div className="text-xs text-dark-300">{memory.value}</div>
                      </div>
                    ))}
                    {!worldDetail.companionMemories?.length && (
                      <p className="text-dark-400 text-sm">No memories recorded.</p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-dark-400 text-sm mb-2">Contacts ({worldDetail.companionContacts?.length || 0})</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {worldDetail.companionContacts?.map((contact) => (
                      <div key={contact.id} className="bg-dark-700 rounded p-3">
                        <div className="text-sm text-dark-200 font-semibold">{contact.name}</div>
                        <div className="text-xs text-dark-300">{contact.relation ?? 'unknown relation'}</div>
                        {contact.notes && (
                          <div className="text-xs text-dark-400">{contact.notes}</div>
                        )}
                      </div>
                    ))}
                    {!worldDetail.companionContacts?.length && (
                      <p className="text-dark-400 text-sm">No contacts recorded.</p>
                    )}
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* Usage Stats */}
          {worldDetail.usageStats && (
            <Section title="Usage Stats">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoItem label="API Requests" value={worldDetail.usageStats.request_count} />
                <InfoItem label="Total Tokens" value={worldDetail.usageStats.total_tokens?.toLocaleString()} />
                <InfoItem label="Total Cost" value={formatCost(worldDetail.usageStats.total_cost_micros || 0)} />
                <InfoItem label="Avg Latency" value={`${Math.round(worldDetail.usageStats.avg_latency_ms || 0)}ms`} />
              </div>
            </Section>
          )}

          {/* Characters */}
          <Section title={`Characters (${worldDetail.characters.length})`}>
            {worldDetail.characters.length === 0 ? (
              <p className="text-dark-400">No characters extracted yet</p>
            ) : (
              <div className="space-y-4">
                {worldDetail.characters.map((char: any) => (
                  <CharacterCard key={char.id} character={char} />
                ))}
              </div>
            )}
          </Section>

          {/* Recent Messages */}
          <Section title={`Recent Messages (${worldDetail.recentMessages.length})`}>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {worldDetail.recentMessages.map((msg: any) => (
                <div 
                  key={msg.id} 
                  className={`rounded p-3 ${
                    msg.role === 'user' 
                      ? 'bg-primary-900/30 border border-primary-800' 
                      : 'bg-dark-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-dark-400 uppercase">{msg.role}</span>
                      <span className="text-xs text-dark-500">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    {msg.role === 'assistant' && msg.inference_log_id && (
                      <button
                        onClick={() => setInspectLogId(msg.inference_log_id)}
                        className="text-xs text-dark-400 hover:text-primary-400 flex items-center gap-1"
                        title="Inspect LLM call"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Inspect
                      </button>
                    )}
                  </div>
                  <p className="text-dark-200 whitespace-pre-wrap text-sm">{msg.content}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Recent Inference */}
          <Section title={`Recent Inference (${worldDetail.recentInference.length})`} defaultOpen={false}>
            <div className="space-y-2">
              {worldDetail.recentInference.map((log: any) => (
                <Link 
                  key={log.id}
                  to={`/admin/inference/${log.id}`}
                  className="block bg-dark-700 rounded p-3 hover:bg-dark-600 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-dark-200">{log.request_type}</span>
                      {log.agent_name && (
                        <span className="text-dark-400 text-sm">({log.agent_name})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-dark-400">
                      <span>{log.prompt_tokens + log.completion_tokens} tokens</span>
                      <span>{log.latency_ms}ms</span>
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
          
          {/* Curation Agent Logs */}
          {worldDetail.curationLogs && worldDetail.curationLogs.length > 0 && (
            <Section title={`Curation Agent Activity (${worldDetail.curationLogs.length})`} defaultOpen={false}>
              <div className="space-y-2">
                {worldDetail.curationLogs.map((log: any) => (
                  <Link 
                    key={log.id}
                    to={`/admin/inference/${log.id}`}
                    className="block bg-dark-700 rounded p-3 hover:bg-dark-600 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-purple-400 font-medium">{log.agent_name}</span>
                        <span className="text-dark-400 text-sm">{log.request_type}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-dark-400">
                        <span>{(log.prompt_tokens || 0) + (log.completion_tokens || 0)} tokens</span>
                        <span>{log.latency_ms}ms</span>
                        <span>{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    {log.error_message && (
                      <div className="mt-2 text-red-400 text-sm">{log.error_message}</div>
                    )}
                  </Link>
                ))}
              </div>
            </Section>
          )}
          
          {/* Scaffolding Trace */}
          {worldDetail.scaffoldingTrace && (
            <Section title="🏗️ World Creation Trace" defaultOpen={false}>
              <Link 
                to={`/admin/worlds/${worldId}/traces/${worldDetail.scaffoldingTrace.id}`}
                className="block bg-dark-700 rounded p-4 hover:bg-dark-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${
                      worldDetail.scaffoldingTrace.status === 'completed' ? 'bg-green-500' :
                      worldDetail.scaffoldingTrace.status === 'failed' ? 'bg-red-500' :
                      'bg-yellow-500 animate-pulse'
                    }`} />
                    <span className="text-primary-400 font-medium">Scaffolding Pipeline</span>
                    <span className="text-dark-400 text-sm">
                      ({worldDetail.scaffoldingTrace.totalCalls || 0} agent calls)
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-dark-400">
                    <span>
                      {((worldDetail.scaffoldingTrace.totalInputTokens || 0) + 
                        (worldDetail.scaffoldingTrace.totalOutputTokens || 0)).toLocaleString()} tokens
                    </span>
                    <span>{(worldDetail.scaffoldingTrace.totalLatencyMs || 0).toLocaleString()}ms</span>
                    <span>{new Date(worldDetail.scaffoldingTrace.startedAt).toLocaleString()}</span>
                  </div>
                </div>
                {worldDetail.scaffoldingTrace.triggerContent && (
                  <div className="mt-2 text-dark-400 text-sm truncate">
                    {worldDetail.scaffoldingTrace.triggerContent}
                  </div>
                )}
              </Link>
            </Section>
          )}
        </div>

        {/* Inspect Modal */}
        {inspectLogId && (
          <InspectModal
            inferenceLogId={inspectLogId}
            onClose={() => setInspectLogId(null)}
          />
        )}
      </AdminLayout>
    );
  }

  // Worlds List View
  return (
    <AdminLayout
      title="Worlds"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Worlds' }]}
      actions={
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-dark-200">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(event) => {
                setIncludeDeleted(event.target.checked);
                setPage(0);
              }}
            />
            Show deleted worlds
          </label>
          <span className="text-dark-400">{total} total</span>
        </div>
      }
    >
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-dark-700">
            <tr>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">World</th>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">User</th>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">Genre/Tone</th>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">Messages</th>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">Characters</th>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">Tokens</th>
              <th className="text-left px-4 py-3 text-dark-300 font-medium">Cost</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-dark-400">Loading...</td>
              </tr>
            ) : worlds.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-dark-400">No worlds found</td>
              </tr>
            ) : (
              worlds.map((w) => (
                <tr key={w.id} className="border-t border-dark-600 hover:bg-dark-700">
                  <td className="px-4 py-3">
                    <Link to={`/admin/worlds/${w.id}`} className="text-primary-400 hover:underline">
                      {w.title}
                    </Link>
                    <div className="text-xs text-dark-500">{w.id}</div>
                    <span className={`inline-block mt-1 rounded px-2 py-0.5 text-xs ${
                      w.status === 'deleted' ? 'bg-red-900/50 text-red-200' : 'bg-dark-700 text-dark-300'
                    }`}>
                      {w.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-dark-300 text-sm">{w.user_email}</td>
                  <td className="px-4 py-3">
                    <span className="text-dark-200">{w.genre || '-'}</span>
                    <span className="text-dark-500 mx-1">/</span>
                    <span className="text-dark-400">{w.tone || '-'}</span>
                  </td>
                  <td className="px-4 py-3 text-dark-200">{w.message_count}</td>
                  <td className="px-4 py-3 text-dark-200">{w.character_count}</td>
                  <td className="px-4 py-3 text-dark-200">{w.total_tokens.toLocaleString()}</td>
                  <td className="px-4 py-3 text-dark-200">{formatCost(w.total_cost_micros)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="flex items-center justify-between mt-4">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary disabled:opacity-50">
            Previous
          </button>
          <span className="text-dark-400">Page {page + 1} of {Math.ceil(total / limit)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total} className="btn-secondary disabled:opacity-50">
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  );
}

function Section({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-dark-800 border border-dark-600 rounded-lg">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-dark-700/50 transition-colors rounded-t-lg"
      >
        <h3 className="text-lg font-semibold text-dark-50">{title}</h3>
        <svg 
          className={`w-5 h-5 text-dark-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-6">
          {children}
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-dark-400 text-sm">{label}</div>
      <div className="text-dark-50 font-medium">{value}</div>
    </div>
  );
}

// Parse JSON safely
function parseJson<T>(value: unknown, fallback: T): T {
  if (!value) return fallback;
  if (Array.isArray(value)) return value as T;
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

// Rich character card component
function CharacterCard({ character }: { character: any }) {
  const [expanded, setExpanded] = useState(false);
  
  const defenseMechanisms = parseJson<string[]>(character.defense_mechanisms, []);
  const blindSpots = parseJson<string[]>(character.blind_spots, []);
  const knowledgeDomains = parseJson<string[]>(character.knowledge_domains, []);
  const observableHabits = parseJson<string[]>(character.observable_habits, []);
  const surfaceGoals = parseJson<string[]>(character.surface_goals, []);
  const hiddenGoals = parseJson<string[]>(character.hidden_goals, []);
  const activeConcerns = parseJson<string[]>(character.active_concerns, []);
  const currentSecrets = parseJson<string[]>(character.current_secrets, []);
  const currentWounds = parseJson<string[]>(character.current_wounds, []);
  const dialogueExamples = parseJson<string[]>(character.canonical_dialogue_examples, []);
  
  return (
    <div className="bg-dark-700 rounded-lg overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-dark-600/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h4 className="font-medium text-dark-50">{character.name}</h4>
          {character.is_player_character && (
            <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-0.5 rounded">Player</span>
          )}
          {character.role_in_story && (
            <span className="text-xs bg-dark-600 text-dark-300 px-2 py-0.5 rounded">{character.role_in_story}</span>
          )}
          {character.age && (
            <span className="text-xs text-dark-400">Age: {character.age}</span>
          )}
        </div>
        <svg 
          className={`w-4 h-4 text-dark-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Summary - always visible */}
      {character.identity_summary && (
        <div className="px-4 pb-3 text-dark-300 text-sm border-b border-dark-600">
          {character.identity_summary}
        </div>
      )}
      
      {/* Expanded details */}
      {expanded && (
        <div className="px-4 py-4 space-y-4 bg-dark-750">
          {/* Physical & Background */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {character.physical_description && (
              <div>
                <div className="text-dark-400 text-xs uppercase mb-1">Physical Description</div>
                <div className="text-dark-200 text-sm">{character.physical_description}</div>
              </div>
            )}
            {character.background && (
              <div>
                <div className="text-dark-400 text-xs uppercase mb-1">Background</div>
                <div className="text-dark-200 text-sm">{character.background}</div>
              </div>
            )}
          </div>
          
          {/* Voice & Speech */}
          {(character.speech_patterns || character.vocabulary_tier || character.speech_tempo) && (
            <div>
              <div className="text-dark-400 text-xs uppercase mb-2">Voice & Speech</div>
              <div className="flex flex-wrap gap-2">
                {character.vocabulary_tier && (
                  <span className="text-xs bg-blue-900/30 text-blue-400 px-2 py-1 rounded">
                    {character.vocabulary_tier}
                  </span>
                )}
                {character.speech_tempo && (
                  <span className="text-xs bg-blue-900/30 text-blue-400 px-2 py-1 rounded">
                    {character.speech_tempo}
                  </span>
                )}
                {character.emotional_baseline && (
                  <span className="text-xs bg-blue-900/30 text-blue-400 px-2 py-1 rounded">
                    {character.emotional_baseline}
                  </span>
                )}
              </div>
              {character.speech_patterns && (
                <div className="text-dark-300 text-sm mt-2">{character.speech_patterns}</div>
              )}
            </div>
          )}
          
          {/* Goals */}
          {(surfaceGoals.length > 0 || hiddenGoals.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {surfaceGoals.length > 0 && (
                <div>
                  <div className="text-dark-400 text-xs uppercase mb-2">Surface Goals</div>
                  <ul className="text-dark-300 text-sm space-y-1">
                    {surfaceGoals.map((goal, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-green-400">→</span> {goal}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {hiddenGoals.length > 0 && (
                <div>
                  <div className="text-dark-400 text-xs uppercase mb-2">Hidden Goals</div>
                  <ul className="text-dark-300 text-sm space-y-1">
                    {hiddenGoals.map((goal, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-yellow-400">⚡</span> {goal}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Psychological Core */}
          {(character.primary_drive || character.core_wound || defenseMechanisms.length > 0 || blindSpots.length > 0) && (
            <div>
              <div className="text-dark-400 text-xs uppercase mb-2">Psychological Core</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-dark-300 text-sm">
                {character.primary_drive && (
                  <div>
                    <div className="text-dark-500 text-xs uppercase mb-1">Primary Drive</div>
                    <div>{character.primary_drive}</div>
                  </div>
                )}
                {character.core_wound && (
                  <div>
                    <div className="text-dark-500 text-xs uppercase mb-1">Core Wound</div>
                    <div>{character.core_wound}</div>
                  </div>
                )}
              </div>
              {(defenseMechanisms.length > 0 || blindSpots.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-dark-300 text-sm">
                  {defenseMechanisms.length > 0 && (
                    <div>
                      <div className="text-dark-500 text-xs uppercase mb-1">Defense Mechanisms</div>
                      <ul className="space-y-1">
                        {defenseMechanisms.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {blindSpots.length > 0 && (
                    <div>
                      <div className="text-dark-500 text-xs uppercase mb-1">Blind Spots</div>
                      <ul className="space-y-1">
                        {blindSpots.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Knowledge & Habits */}
          {(knowledgeDomains.length > 0 || observableHabits.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {knowledgeDomains.length > 0 && (
                <div>
                  <div className="text-dark-400 text-xs uppercase mb-2">Knowledge Domains</div>
                  <div className="flex flex-wrap gap-2">
                    {knowledgeDomains.map((item, i) => (
                      <span key={i} className="text-xs bg-dark-600 text-dark-200 px-2 py-1 rounded">{item}</span>
                    ))}
                  </div>
                </div>
              )}
              {observableHabits.length > 0 && (
                <div>
                  <div className="text-dark-400 text-xs uppercase mb-2">Observable Habits</div>
                  <div className="flex flex-wrap gap-2">
                    {observableHabits.map((item, i) => (
                      <span key={i} className="text-xs bg-dark-600 text-dark-200 px-2 py-1 rounded">{item}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Current State */}
          {(character.current_objective || character.current_emotional_state || activeConcerns.length > 0) && (
            <div>
              <div className="text-dark-400 text-xs uppercase mb-2">Current State</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-dark-300 text-sm">
                {character.current_objective && (
                  <div>
                    <div className="text-dark-500 text-xs uppercase mb-1">Objective</div>
                    <div>{character.current_objective}</div>
                  </div>
                )}
                {character.current_emotional_state && (
                  <div>
                    <div className="text-dark-500 text-xs uppercase mb-1">Emotional State</div>
                    <div>{character.current_emotional_state}</div>
                  </div>
                )}
              </div>
              {activeConcerns.length > 0 && (
                <div className="mt-3">
                  <div className="text-dark-500 text-xs uppercase mb-1">Active Concerns</div>
                  <ul className="text-dark-300 text-sm space-y-1">
                    {activeConcerns.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Secrets & Wounds */}
          {(currentSecrets.length > 0 || currentWounds.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentSecrets.length > 0 && (
                <div>
                  <div className="text-dark-400 text-xs uppercase mb-2">Current Secrets</div>
                  <ul className="text-dark-300 text-sm space-y-1">
                    {currentSecrets.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {currentWounds.length > 0 && (
                <div>
                  <div className="text-dark-400 text-xs uppercase mb-2">Current Wounds</div>
                  <ul className="text-dark-300 text-sm space-y-1">
                    {currentWounds.map((item, i) => (
                      <li key={i}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Dialogue Examples */}
          {dialogueExamples.length > 0 && (
            <div>
              <div className="text-dark-400 text-xs uppercase mb-2">Canonical Dialogue</div>
              <ul className="text-dark-300 text-sm space-y-1">
                {dialogueExamples.map((line, i) => (
                  <li key={i} className="italic">"{line}"</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Meta info */}
          <div className="text-xs text-dark-500 pt-2 border-t border-dark-600 flex gap-4">
            <span>ID: {character.id}</span>
            {character.scene_count > 0 && <span>Scenes: {character.scene_count}</span>}
            {character.last_seen_turn !== null && <span>Last seen turn: {character.last_seen_turn}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
